import type { ReactNode } from 'react';
import { HakenIcon } from '../icons';

export interface FortschrittSchritt {
	key: string;
	label: string;
	icon: ReactNode;
}

interface FortschrittProps {
	schritte: FortschrittSchritt[];
	aktiverIndex: number;
	/**
	 * Der am weitesten je erreichte Schritt in dieser Sitzung — nicht
	 * derselbe wie `aktiverIndex`. Ohne diese Unterscheidung würde ein
	 * Rücksprung (z. B. zurück zu "Räume") alle später schon besuchten
	 * Schritte (Zähler, Schlüssel, …) wieder aus dem Stepper unerreichbar
	 * machen, obwohl ihre Daten längst erfasst sind.
	 */
	maxErreichterIndex: number;
	onSpringeZu?: (index: number) => void;
}

/**
 * Horizontale Schritt-Anzeige über dem gesamten Ablauf — Räume, Zähler,
 * Schlüssel, Unterschriften, Dokument. Jeder je erreichte Schritt bleibt
 * anklickbar, egal wo man gerade steht; nur Schritte, die noch nie besucht
 * wurden, sind gesperrt (kein Überspringen ganz nach vorn).
 */
export function Fortschritt({ schritte, aktiverIndex, maxErreichterIndex, onSpringeZu }: FortschrittProps) {
	return (
		<nav className="fortschritt" aria-label="Ablauf der Übergabe">
			{schritte.map((schritt, index) => {
				const erledigt = index < maxErreichterIndex;
				const aktiv = index === aktiverIndex;
				const erreichbar = index <= maxErreichterIndex;
				return (
					<div key={schritt.key} className="fortschritt-schritt">
						<button
							type="button"
							className={`fortschritt-punkt ${erledigt ? 'ist-erledigt' : ''} ${aktiv ? 'ist-aktiv' : ''}`}
							disabled={!erreichbar}
							onClick={() => erreichbar && onSpringeZu?.(index)}
							aria-current={aktiv ? 'step' : undefined}
							aria-label={schritt.label}
						>
							{erledigt ? <HakenIcon width={16} height={16} /> : schritt.icon}
						</button>
						<span className={`fortschritt-label ${aktiv ? 'ist-aktiv' : ''}`}>{schritt.label}</span>
						{index < schritte.length - 1 && (
							<div className={`fortschritt-linie ${erledigt ? 'ist-erledigt' : ''}`} />
						)}
					</div>
				);
			})}
		</nav>
	);
}
